/**
 * Per-corner border-radius helpers.
 *
 * Background
 * ----------
 * Historically every shape carried a single `props.rounded` number applied
 * uniformly to all four corners (`rx`/`ry` on SVG `<rect>`, single-value
 * `border-radius` on HTML). The Alignment panel ships a second control that
 * lets users override each corner independently — that override lives in
 * four optional fields on the shape props:
 *
 *   - `roundedTL` (top-left)
 *   - `roundedTR` (top-right)
 *   - `roundedBR` (bottom-right)
 *   - `roundedBL` (bottom-left)
 *
 * When a per-corner field is `undefined`, the shape falls back to
 * `props.rounded` for that corner. This keeps existing shapes (created
 * before the feature shipped) rendering exactly as before — the per-corner
 * UI only takes effect when a user explicitly edits one of the four
 * inputs.
 *
 * All shapes that surface a per-corner control should add these four
 * optional props to their type / `getDefaultProps` (leaving the defaults
 * as `undefined`), then read radii through `getCornerRadii(props)` and
 * emit CSS / SVG via the helpers below.
 */

export type CornerRadii = {
  tl: number;
  tr: number;
  br: number;
  bl: number;
};

/** Read per-corner radii from a shape's `props`, falling back to the
 *  shared `props.rounded` value when any individual corner is unset. */
export function getCornerRadii(props: any): CornerRadii {
  const fallback = Math.max(0, Number(props?.rounded || 0));
  const pick = (override: any): number => {
    if (override === undefined || override === null) return fallback;
    const n = Number(override);
    return Number.isFinite(n) ? Math.max(0, n) : fallback;
  };
  return {
    tl: pick(props?.roundedTL),
    tr: pick(props?.roundedTR),
    br: pick(props?.roundedBR),
    bl: pick(props?.roundedBL),
  };
}

/** Clamp each corner so adjacent corners can't overlap on a tiny shape. */
export function clampCornerRadii(r: CornerRadii, w: number, h: number): CornerRadii {
  const max = Math.max(0, Math.min(w, h) / 2);
  return {
    tl: Math.min(r.tl, max),
    tr: Math.min(r.tr, max),
    br: Math.min(r.br, max),
    bl: Math.min(r.bl, max),
  };
}

/** True iff all four corners share the same radius — caller can short-
 *  circuit to a cheaper `<rect rx ry>` / single-value CSS in that case. */
export function areCornersUniform(r: CornerRadii): boolean {
  return r.tl === r.tr && r.tr === r.br && r.br === r.bl;
}

/** CSS `border-radius` shorthand. Always emits the 4-value form so the
 *  browser doesn't apply its own uniform interpretation. */
export function cornerRadiiCss(r: CornerRadii): string {
  return `${r.tl}px ${r.tr}px ${r.br}px ${r.bl}px`;
}

/**
 * Build the SVG `d` string for a rounded rectangle with per-corner radii.
 *
 * The path runs clockwise starting at the top edge just after the TL
 * corner, drawing each side and using an `A` (elliptical arc) command for
 * the rounded corner. When a corner's radius is 0 the `A 0 0` collapses
 * to a straight line per the SVG spec, so the path is correct for any
 * mix of rounded / square corners.
 */
export function roundedRectPath(x: number, y: number, w: number, h: number, r: CornerRadii): string {
  const c = clampCornerRadii(r, w, h);
  return [
    `M ${x + c.tl} ${y}`,
    `H ${x + w - c.tr}`,
    `A ${c.tr} ${c.tr} 0 0 1 ${x + w} ${y + c.tr}`,
    `V ${y + h - c.br}`,
    `A ${c.br} ${c.br} 0 0 1 ${x + w - c.br} ${y + h}`,
    `H ${x + c.bl}`,
    `A ${c.bl} ${c.bl} 0 0 1 ${x} ${y + h - c.bl}`,
    `V ${y + c.tl}`,
    `A ${c.tl} ${c.tl} 0 0 1 ${x + c.tl} ${y}`,
    "Z",
  ].join(" ");
}

// ─────────────────────────────────────────────────────────────────────
// Triangle (3-corner) variants
// ─────────────────────────────────────────────────────────────────────
//
// `customTriangle` (a.k.a. the "Polygon" tool) is a 3-vertex shape, so
// it doesn't fit the TL/TR/BR/BL model. We model its three corners
// explicitly:
//
//   top -> the apex at (w/2, 0)
//   br  -> the bottom-right corner at (w, h)
//   bl  -> the bottom-left corner at (0, h)
//
// Each corner reads from its own optional override
// (`roundedTop`/`roundedBR`/`roundedBL`), falling back to the uniform
// `rounded` value.

export type TriangleCornerRadii = {
  top: number;
  br: number;
  bl: number;
};

/** Read per-corner radii for a customTriangle shape. Same fallback
 *  semantics as `getCornerRadii` — missing overrides inherit from
 *  `props.rounded`. */
export function getTriangleCornerRadii(props: any): TriangleCornerRadii {
  const fallback = Math.max(0, Number(props?.rounded || 0));
  const pick = (override: any): number => {
    if (override === undefined || override === null) return fallback;
    const n = Number(override);
    return Number.isFinite(n) ? Math.max(0, n) : fallback;
  };
  return {
    top: pick(props?.roundedTop),
    br: pick(props?.roundedBR),
    bl: pick(props?.roundedBL),
  };
}

export function areTriangleCornersUniform(r: TriangleCornerRadii): boolean {
  return r.top === r.br && r.br === r.bl;
}

/**
 * Build a SVG `d` string for a rounded isoceles triangle that fits in
 * the (w × h) box with apex centered on the top edge. Each corner can
 * carry its own radius; the helper offsets the start / end of each
 * straight side per the local corner's radius and joins them with a
 * quadratic Bézier through the actual vertex (the same approach the
 * original triangle util used uniformly).
 *
 * Each side's two offsets are clamped to half the side's length so
 * adjacent rounded segments can't cross over the midpoint and produce
 * a self-intersecting outline. This matches what the original
 * `getRoundedPoint(p1, p2, rounded)` did with a single shared radius.
 */
export function roundedTrianglePath(w: number, h: number, r: TriangleCornerRadii): string {
  // Vertices, ordered clockwise from the apex.
  const top = { x: w / 2, y: 0 };
  const br = { x: w, y: h };
  const bl = { x: 0, y: h };

  // Compute "offset from corner A toward corner B by radius rA, and
  // from corner B back toward A by radius rB". Each offset is capped
  // at half the edge length so the two offsets on a side can't cross
  // each other and self-intersect the outline.
  const offsetEdge = (a: { x: number; y: number }, b: { x: number; y: number }, rA: number, rB: number) => {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len === 0) return { fromA: { ...a }, fromB: { ...b } };
    const cappedA = Math.min(rA, len / 2);
    const cappedB = Math.min(rB, len / 2);
    const ux = dx / len;
    const uy = dy / len;
    return {
      fromA: { x: a.x + ux * cappedA, y: a.y + uy * cappedA },
      fromB: { x: b.x - ux * cappedB, y: b.y - uy * cappedB },
    };
  };

  // Edges (clockwise): top → br, br → bl, bl → top.
  const eRight = offsetEdge(top, br, r.top, r.br); // right side
  const eBottom = offsetEdge(br, bl, r.br, r.bl); // bottom
  const eLeft = offsetEdge(bl, top, r.bl, r.top); // left side

  // Path: start at the entry to the top corner (on the left side,
  // just below the apex), arc through the apex, straight along the
  // right side to the BR entry, arc through BR, straight along the
  // bottom to BL entry, arc through BL, back to start.
  return [
    `M ${eLeft.fromB.x},${eLeft.fromB.y}`,
    `Q ${top.x},${top.y} ${eRight.fromA.x},${eRight.fromA.y}`,
    `L ${eRight.fromB.x},${eRight.fromB.y}`,
    `Q ${br.x},${br.y} ${eBottom.fromA.x},${eBottom.fromA.y}`,
    `L ${eBottom.fromB.x},${eBottom.fromB.y}`,
    `Q ${bl.x},${bl.y} ${eLeft.fromA.x},${eLeft.fromA.y}`,
    "Z",
  ].join(" ");
}
